import React, { ReactNode, useCallback } from "react";
import { InputGroup, Tooltip, OverlayTrigger } from "react-bootstrap";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

interface CheckboxProps {
  checkboxLabel?: ReactNode;
  required?: boolean;
  children?: ReactNode;
  name: string;
  disabled?: boolean;
  dataTestId?: string;
  providerSections?: boolean;
  tooltipText?: boolean;
  rateCardText?: boolean;
  customOnChange?: (...params: any[]) => void;
  checked?: boolean;
  checkByDefault: boolean;
  validations?: object;
}

const Checkbox = function ({
  checkboxLabel,
  children,
  name,
  required,
  disabled,
  dataTestId,
  rateCardText,
  tooltipText,
  customOnChange,
  checkByDefault,
  checked,
  validations,
  ...props
}: CheckboxProps) {
  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext();

  function constructErrorName() {
    let n = name.split(/[[.]+/).join(",").split(/]|,/);
    n = n.filter((each: string) => each.length > 0);
    return errors?.[n[0]]?.[n[1]]?.[n[2]];
  }

  const GetInput = useCallback(
    () => (
      <>
        <label className="d-flex align-items-center cursor-pointer mb-0 w-100">
          <input
            type="checkbox"
            className="d-none"
            {...register(name, { required, ...validations })}
            {...(checkByDefault && { defaultChecked: getValues(name) ? undefined : checkByDefault })}
            disabled={disabled || false}
            {...(customOnChange && {
              checked: watch(name),
              onChange: (e) => {
                if (customOnChange) customOnChange();
                setValue(name, e.target.checked);
              },
            })}
            data-testid={dataTestId}
          />
          <span
            className={`border-dark border-solid border-1 mr-11 position-relative br-3 flex-none ${
              disabled ? "checkbox__box__disabled" : "checkbox__box"
            }`}
          />
          <span style={{ color: "black" }} className={!tooltipText ? "mr-auto" : ""}>
            {checkboxLabel}
          </span>

          {tooltipText && (
            <div className="pl-3">
              <OverlayTrigger
                key="right"
                placement="right"
                overlay={
                  <Tooltip id={`tooltip-${name}`}>
                    <strong>{tooltipText}</strong>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="cursor-pointer font-b1 mt-1" />
              </OverlayTrigger>
            </div>
          )}

          {rateCardText && (
            <span className="text-success font-weight-bold">
              <FormattedMessage id="components.atoms.checkbox.rateCardText" defaultMessage="Rate from file available" />
            </span>
          )}
        </label>

        {checked || getValues(name) ? <div>{children}</div> : null}
      </>
    ),
    [
      name,
      required,
      checkByDefault,
      disabled,
      customOnChange,
      dataTestId,
      checkboxLabel,
      tooltipText,
      rateCardText,
      checked,
      getValues(name),
      children,
    ],
  );

  return (
    <>
      {props.providerSections ? (
        <InputGroup className="checkbox bg-transparent">
          <GetInput />
        </InputGroup>
      ) : (
        <InputGroup className="checkbox bg-transparent">
          <div
            className={`${
              required && constructErrorName() ? "border-danger" : "form-control"
            } border-1 border-solid br-5 pt-10 pb-10 pl-14 pr-14 bg-transparent w-100 height-auto`}
          >
            <GetInput />
          </div>
        </InputGroup>
      )}
      {required
        ? errors[name] && (
            <div className="d-block font-b3 invalid-feedback">
              <FormattedMessage
                id="components.atoms.checkbox.requiredFieldMsg"
                defaultMessage="{name} is a required field"
                values={{ name }}
              />
            </div>
          )
        : null}
    </>
  );
};

Checkbox.propTypes = {
  checkboxLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  required: PropTypes.bool,
  children: PropTypes.node,
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  disabled: PropTypes.bool,
  dataTestId: PropTypes.string,
  providerSections: PropTypes.bool,
  tooltipText: PropTypes.string,
  rateCardText: PropTypes.bool,
  customOnChange: PropTypes.func,
  checked: PropTypes.bool,
  checkByDefault: PropTypes.bool,
};

Checkbox.defaultProps = {
  checkboxLabel: "",
  checkByDefault: false,
  required: false,
  children: null,
  disabled: false,
  dataTestId: "",
  providerSections: false,
  tooltipText: "",
  rateCardText: false,
  customOnChange: null,
  checked: false,
  validations: undefined,
};

export default Checkbox;
